import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { NotificationServiceFactory } from '../services/NotificationService';
import { useAuth } from '../auth/AuthContext';
import { toast } from 'react-toastify';

const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  const { user, apiFetch } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  
  // Debug isConnected state changes
  useEffect(() => {
    console.log(`NotificationContext: isConnected state changed to: ${isConnected}`);
  }, [isConnected]);
  const [service, setService] = useState(null);

  // Handle incoming notifications
  const handleNotification = useCallback((notification) => {
    setNotifications(prev => [notification, ...prev]);
    setUnreadCount(prev => prev + 1);
    
    // Show browser notification if permission granted
    if (Notification.permission === 'granted') {
      new Notification(notification.title, {
        body: notification.message,
        icon: '/favicon.ico',
        tag: `notification-${notification.id}`
      });
    }

    // Show toast notification only if show_toast is true in metadata
    const shouldShowToast = notification.metadata && notification.metadata.show_toast === true;
    if (shouldShowToast) {
      const toastType = notification.type === 'error' ? 'error' : 
                       notification.type === 'success' ? 'success' : 
                       notification.type === 'warning' ? 'warning' : 'info';
      
      toast[toastType](notification.message, {
        position: "top-right",
        autoClose: 5000,
      });
    }
  }, []);

  // Load notifications from API
  const loadNotifications = useCallback(async () => {
    try {
      const response = await apiFetch('/notifications?limit=50');
      if (response.ok) {
        const data = await response.json();
        setNotifications(data);
        setUnreadCount(data.filter(n => !n.is_read).length);
      }
    } catch (error) {
      console.error('Failed to load notifications:', error);
    }
  }, [apiFetch]);

  // Initialize notification service
  useEffect(() => {
    console.log('NotificationContext: useEffect triggered with user:', user);
    
    if (!user || !user.user_id) {
      console.log('NotificationContext: No user or user_id, clearing notifications');
      // Clear notifications and disconnect if user is not authenticated
      if (service) {
        service.unsubscribe(null);
        setService(null);
      }
      setNotifications([]);
      setUnreadCount(0);
      setIsConnected(false);
      return;
    }

    const initializeService = async () => {
      try {
        console.log('NotificationContext: Creating notification service for user:', user.user_id);
        const notificationService = NotificationServiceFactory.create();
        setService(notificationService);

        // Initialize service (this now just returns immediately)
        await notificationService.initialize();
        console.log('Notification service initialized');

        // Subscribe to notifications (this creates the WebSocket connection)
        console.log('NotificationContext: Subscribing to notifications...');
        await notificationService.subscribe(
          user.user_id, 
          handleNotification,
          (connected) => {
            console.log(`NotificationContext: Connection status changed to ${connected ? 'connected' : 'disconnected'}`);
            console.log(`NotificationContext: Setting isConnected to ${connected}`);
            setIsConnected(connected);
          }
        );
        console.log('Notification service subscription initiated');

        // Browser notification permission is not requested automatically
        // Users can enable it manually if they want browser notifications

        // Load existing notifications
        await loadNotifications();

      } catch (error) {
        console.error('Failed to initialize notification service:', error);
        setIsConnected(false);
      }
    };

    initializeService();

    return () => {
      if (service) {
        service.unsubscribe(user.user_id);
      }
    };
  }, [user, handleNotification, loadNotifications]);

  // Browser notification permission is not requested automatically
  // This prevents the browser alert from appearing

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      const response = await apiFetch(`/notifications/${notificationId}/read`, {
        method: 'POST'
      });
      
      if (response.ok) {
        setNotifications(prev => 
          prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    try {
      const response = await apiFetch('/notifications/mark-all-read', {
        method: 'POST'
      });
      
      if (response.ok) {
        setNotifications(prev => 
          prev.map(n => ({ ...n, is_read: true }))
        );
        setUnreadCount(0);
      }
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  };

  // Refresh notifications
  const refreshNotifications = async () => {
    await loadNotifications();
  };

  // Get unread count
  const getUnreadCount = async () => {
    try {
      const response = await apiFetch('/notifications/unread-count');
      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.unread_count);
      }
    } catch (error) {
      console.error('Failed to get unread count:', error);
    }
  };

  const value = {
    notifications,
    unreadCount,
    isConnected,
    markAsRead,
    markAllAsRead,
    refreshNotifications,
    getUnreadCount,
    loadNotifications
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};
